// src/pages/Admin.js
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";

import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import { db } from "../firebase";

const StatCard = ({ label, value, loading }) => (
  <Card>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Admin = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats] = useState({ users: null, orders: null, customers: null });
  const [users, setUsers] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [activating, setActivating] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [usersSnap, ordersSnap, customersSnap] = await Promise.all([
          getCountFromServer(collection(db, "users")),
          getCountFromServer(collection(db, "orders")),
          getCountFromServer(collection(db, "customers"))
        ]);
        setStats({
          users: usersSnap.data().count,
          orders: ordersSnap.data().count,
          customers: customersSnap.data().count
        });
      } catch (err) {
        console.error("Erreur stats admin:", err);
      } finally {
        setLoadingStats(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const q = query(
          collection(db, "users"),
          orderBy("createdAt", "desc"),
          limit(50)
        );
        const snap = await getDocs(q);
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Erreur liste users admin:", err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchStats();
    fetchUsers();
  }, []);

  const handleToggleActive = async user => {
    setActivating(user.id);
    const newStatus = !user.isActive;
    try {
      await updateDoc(doc(db, "users", user.id), { isActive: newStatus });

      // Envoi de l'email d'activation si on active le compte
      if (newStatus) {
        try {
          const functions = getFunctions();
          await httpsCallable(functions, "sendAccountActivated")({
            email: user.email,
            username: user.displayName || user.email
          });
        } catch {
          // Echec silencieux — l'activation Firestore reste valide
        }
      }

      setUsers(prev =>
        prev.map(u => u.id === user.id ? { ...u, isActive: newStatus } : u)
      );
      enqueueSnackbar(
        newStatus ? `Compte activé : ${user.email}` : `Compte désactivé : ${user.email}`,
        { variant: newStatus ? "success" : "warning" }
      );
    } catch (err) {
      enqueueSnackbar("Erreur lors de la mise à jour du compte", { variant: "error" });
    } finally {
      setActivating(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Administration
      </Typography>

      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={4}>
          <StatCard label="Utilisateurs" value={stats.users} loading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Prestations" value={stats.orders} loading={loadingStats} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Clients (total)" value={stats.customers} loading={loadingStats} />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="h6" fontWeight="bold" mb={2}>
        Utilisateurs ({users.length})
      </Typography>

      {loadingUsers ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Langue</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell align="center">Activer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.language?.toUpperCase() || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? "Actif" : "Inactif"}
                      color={user.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.createdAt?.toDate
                      ? user.createdAt.toDate().toLocaleDateString("fr-FR")
                      : "—"}
                  </TableCell>
                  <TableCell align="center">
                    {activating === user.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Tooltip title={user.isActive ? "Désactiver le compte" : "Activer le compte"}>
                        <IconButton size="small" onClick={() => handleToggleActive(user)}>
                          {user.isActive
                            ? <CheckCircle color="success" fontSize="small" />
                            : <RadioButtonUnchecked color="disabled" fontSize="small" />
                          }
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Admin;
